import { useMemo } from 'react';

import { selectors, useAppSelector } from 'state/global-state';

import { useNotificationCenterSession } from './use-notification-center-session';
import { getEligibleNotificationIds, getReadContentCardsFromLocalStorage } from './utils';

/**
 * Shared by the unread badge and Notification Inbox so they never disagree about whether it's
 * safe to render a read/unread classification yet.
 *
 * `isReady` requires: either a network-sourced content-cards response (`contentCardsLoaded`)
 * or at least one news-feed card already in the slice (Braze SDK cache can paint the feed
 * while a post-`changeUser` refresh is still outstanding). Cards without a `notification-id`
 * still count as "something is in the slice"; they are just omitted from `eligibleNotificationIds`
 * and therefore do not wait on read-status. Then identity must be settled (not mid auth-restore,
 * which also reads as "anonymous"), and then either anonymous, no eligible ids, or a read-status
 * query that has resolved covering every currently-eligible id.
 *
 * `readNotificationIds` is always computed (local storage + any mutation-confirmed ids as a
 * fallback until resolved) - `isReady` only gates whether a consumer may render it.
 */
export const useNotificationCenterReadState = () => {
  const newsFeedContentCards = useAppSelector(selectors.loyalty.selectNewsFeedContentCards);
  const serverReadNotificationIds = useAppSelector(
    selectors.loyalty.selectServerReadNotificationIds
  );
  const serverReadStatus = useAppSelector(selectors.loyalty.selectServerReadStatus);
  const resolvedNotificationIds = useAppSelector(selectors.loyalty.selectResolvedNotificationIds);
  const contentCardsLoaded = useAppSelector(selectors.loyalty.selectContentCardsLoaded);
  const localReadsVersion = useAppSelector(selectors.loyalty.selectLocalReadsVersion);
  const { sessionIdentity, isIdentityResolving, isCurrent } = useNotificationCenterSession();

  const eligibleNotificationIds = useMemo(() => getEligibleNotificationIds(newsFeedContentCards), [
    newsFeedContentCards,
  ]);

  const isAnonymous = !isIdentityResolving && sessionIdentity === null;

  const isCoveredByResolvedQuery =
    isCurrent &&
    serverReadStatus === 'resolved' &&
    eligibleNotificationIds.every(id => resolvedNotificationIds.includes(id));

  const isReady =
    (contentCardsLoaded || newsFeedContentCards.length > 0) &&
    (isAnonymous || eligibleNotificationIds.length === 0 || isCoveredByResolvedQuery);

  const readNotificationIds = useMemo(() => {
    if (isCoveredByResolvedQuery) {
      return serverReadNotificationIds;
    }
    const confirmedIds = isCurrent ? serverReadNotificationIds : [];
    return [...new Set([...confirmedIds, ...getReadContentCardsFromLocalStorage()])];
    // localReadsVersion busts this memo when local storage changes (not otherwise reactive).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoveredByResolvedQuery, isCurrent, serverReadNotificationIds, localReadsVersion]);

  return { isReady, readNotificationIds };
};
